import {
  ForbiddenError,
  NetworkError,
  UnauthorizedError,
  UnprocessableEntityError,
  errorCodes,
} from "@exceptions";
import * as Sentry from "@sentry/react";
import type { QuotaExceededPayload } from "@src/modules/member/context/limit-modal-context";
import { emitQuotaExceeded } from "@src/modules/member/context/limit-modal-context/emitter";
import {
  Mutation,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PostHogProvider } from "posthog-js/react";
import React from "react";
import ReactDOM from "react-dom/client";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { toast } from "react-toastify";

import App from "./App.tsx";
import "./index.css";

Sentry.init({
  enabled: import.meta.env.VITE_SENTRY_ENABLED === "true",
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  dsn: "https://0e900a11d78b803a4efe8485c60da2bc@o4507714180939776.ingest.us.sentry.io/4508234625122304",
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: import.meta.env.VITE_SENTRY_SAMPLE_RATE,
  tracePropagationTargets: [/^https:\/\/core\.(staging\.)?invup\.me\/api/],
  replaysSessionSampleRate: 0.0005,
  replaysOnErrorSampleRate: 1.0,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
      throwOnError: (error) => {
        if (
          error instanceof ForbiddenError ||
          error instanceof UnauthorizedError ||
          error instanceof NetworkError
        ) {
          return false;
        }

        return true;
      },
    },
  },
  queryCache: new QueryCache({}),
  mutationCache: new MutationCache({
    onError: (
      error: Error,
      _: unknown,
      __: unknown,
      mutation: Mutation<unknown, unknown, unknown, unknown>,
    ) => {
      const meta = mutation.meta;
      const errorKey = meta?.errorKey;

      if (
        error instanceof ForbiddenError &&
        typeof error.responseBody === "object" &&
        error.responseBody !== null &&
        (error.responseBody as { error?: string }).error === "quota_exceeded"
      ) {
        const payload = (error.responseBody as { data?: QuotaExceededPayload })
          .data;
        if (payload) {
          emitQuotaExceeded(payload);
          return;
        }
      }

      if (errorKey && error instanceof UnprocessableEntityError) {
        queryClient.setQueryData(["ERROR", errorKey], error);
      } else if (
        !errorKey &&
        error instanceof ForbiddenError &&
        error.errorCode !== errorCodes.EMAIL_VERIFICATION_REQUIRED
      ) {
        toast.error(error.message);
      }
    },
  }),
});

const postHogOptions = {
  api_host: import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_HOST,
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_KEY ? (
      <PostHogProvider
        apiKey={import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
        options={postHogOptions}
      >
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </PostHogProvider>
    ) : (
      <QueryClientProvider client={queryClient}>
        <App />
        {import.meta.env.VITE_ENVIRONMENT === "local" && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    )}
  </React.StrictMode>,
);
